import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { withTheme } from 'styled-components';
import { mapIntegrationLibError } from 'groverPaymentSDK';

import ggAuth from 'ggApp/modules/auth';
import { translationFunctionSelector } from 'ggApp/modules/translator';
import { promiseTimeout as sleepFor } from 'ggApp/utils/promise';

import { PaymentSdkContext } from 'ggApp/shared/common/Payment/PaymentGatewayList/state/paymentSdkContext';

import CreditCardFormUi from './CreditCardFormUi';

import { defaultErrorState, isFormValid, validateFormField } from '../validation';

const READ_3DS_REDIRECT_NOTICE_TIME = 2000;

const generateToken = (formState, paymentLibrary) =>
    new Promise((resolve, reject) => {
        const [expirationMonth, expirationYear] = formState.cardExpiration.split('/');
        const data = {
            card_holder: formState.cardholderName,
            month: expirationMonth.trim(),
            year: `20${expirationYear.trim()}`,
        };
        paymentLibrary.tokenize(
            data,
            (token) => resolve(token),
            (errors) => reject(errors.map(mapIntegrationLibError)),
        );
    });

const mapStateToProps = createStructuredSelector({
    translationFunction: translationFunctionSelector,
    user: ggAuth.selectors.user,
});

const CreditCardForm = ({
    gateway,
    onPaymentMethodCreated,
    translationFunction,
    user,
    uiComponent,
}) => {
    const {
        meta: { public_api_key: publicKey },
    } = gateway;

    const sdkInstance = useContext(PaymentSdkContext);
    const [paymentLibrary, setPaymentLibrary] = useState();
    const [isFormLoading, setFormLoadingState] = useState(true);
    const [isFormSubmitting, setFormSubmittingState] = useState(false);
    const [paymentLibraryLoadFailed, setPaymentLibraryLoadError] = useState(false);
    const [paymentMethodCreationFailed, setPaymentMethodCreationError] = useState(false);
    const [is3dsRequired, set3dsRequired] = useState(false);
    const [tokenizationErrors, setTokenizationErrors] = useState(null);
    const [formState, setFormState] = useState({
        cardholderName: '',
        cardExpiration: '',
        cardData: {},
    });
    const [formErrorState, setFormErrorState] = useState(defaultErrorState);

    const onInput = useCallback((event) => {
        const { target } = event;
        const { name, value } = target;

        setFormState((previousFormState) => ({
            ...previousFormState,
            [name]: value,
        }));
    }, []);

    const handleSubmit = useCallback(
        (event) => {
            event.preventDefault();
            // TODO: replace with SonntagButton or fix in @grover/ui
            // current Button does not pass the `disabled` arg to the
            // button itself
            if (!isFormValid(formState)) {
                return;
            }
            setFormErrorState(defaultErrorState);
            setFormSubmittingState(true);
            generateToken(formState, paymentLibrary)
                .then(
                    (token) => sdkInstance.createPaymentMethod(gateway, user.id, { token }),
                    (errors) => setTokenizationErrors(errors),
                )
                .catch((error) => {
                    setPaymentMethodCreationError(true);
                    setFormSubmittingState(false);
                    return Promise.reject(error);
                })
                .then((result) => {
                    const { checkoutUrl, paymentMethodId, billingAccountId } = result || {};
                    if (checkoutUrl) {
                        set3dsRequired(true);
                        // keep the 3ds redirect message showing for customer to be able to read it
                        return sleepFor(READ_3DS_REDIRECT_NOTICE_TIME).then(() => {
                            // perform a redirect to the PSP page
                            window.location.href = checkoutUrl;
                            // keep the receiver waiting while browser performs navigation
                            return sleepFor(30000);
                        });
                    }
                    setFormSubmittingState(false);
                    return onPaymentMethodCreated(paymentMethodId, billingAccountId);
                });
        },
        [formState, paymentLibrary, user, gateway, sdkInstance, onPaymentMethodCreated],
    );

    const handleIframeInputBlur = useCallback(
        (fieldName) => {
            const fieldErrorState = validateFormField(fieldName, formState);
            setFormErrorState((previousFormErrorState) => ({
                ...previousFormErrorState,
                [fieldName]: fieldErrorState,
            }));
        },
        [formState],
    );

    const handleInputBlur = useCallback(
        (event) => {
            const fieldName = event.target.name;
            handleIframeInputBlur(fieldName);
        },
        [handleIframeInputBlur],
    );

    useEffect(() => {
        const libraryInputHandler = (cardData) =>
            setFormState((previousFormState) => ({
                ...previousFormState,
                cardData,
            }));
        const setInputHandlers = (libInstance) => {
            libInstance.numberOn('input', libraryInputHandler);
            libInstance.cvvOn('input', libraryInputHandler);
            setPaymentLibrary(libInstance);
            setFormLoadingState(false);
        };
        const setupForm = () => {
            sdkInstance
                .getPaymentLibrary()
                .init(publicKey, 'myRandomId-cardNumber', 'myRandomId-cvv', setInputHandlers);
        };
        const failForm = (error) => {
            setPaymentLibraryLoadError(error);
            setFormLoadingState(false);
        };

        sdkInstance
            .loadPaymentLibrary()
            .then(setupForm)
            .catch(failForm);
    }, [sdkInstance, publicKey, uiComponent]);

    const CreditCardFormUiComponent = uiComponent || CreditCardFormUi;

    return (
        <CreditCardFormUiComponent
            isFormLoading={isFormLoading}
            isFormValid={isFormValid}
            paymentLibraryLoadFailed={paymentLibraryLoadFailed}
            handleSubmit={handleSubmit}
            is3dsRequired={is3dsRequired}
            translationFunction={translationFunction}
            formErrorState={formErrorState}
            formState={formState}
            paymentLibrary={paymentLibrary}
            handleIframeInputBlur={handleIframeInputBlur}
            onInput={onInput}
            handleInputBlur={handleInputBlur}
            isFormSubmitting={isFormSubmitting}
            tokenizationErrors={tokenizationErrors}
            paymentMethodCreationFailed={paymentMethodCreationFailed}
        />
    );
};

CreditCardForm.propTypes = {
    gateway: PropTypes.shape({
        meta: PropTypes.shape({
            public_api_key: PropTypes.string,
        }).isRequired,
    }).isRequired,
    onPaymentMethodCreated: PropTypes.func.isRequired,
    translationFunction: PropTypes.func.isRequired,
};

export default connect(mapStateToProps)(withTheme(CreditCardForm));
